import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { docsErr } from "@/lib/docsApi";
import { FilledPages } from "@/components/docs/FilledPages";

const btn = "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-40";
const keyOf = (f) => f.label.trim().toLowerCase().replace(/\s+/g, " ");

/** Pop-up: answer each blank once (shared across every document), adopt a script signature once, sign them all. */
export function SignWizard({ api, base, docs: liveDocs, seed = {}, open, onClose, prefix = "sign-wizard" }) {
  const [docs, setDocs] = useState([]);
  const [forms, setForms] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState([]);
  const [errors, setErrors] = useState([]);
  useEffect(() => {
    if (!open) return;
    const docs = liveDocs;
    setDocs(docs); setForms(null); setStep(0); setDone([]); setErrors([]); setAgree(false); setReadOk({});
    Promise.all(docs.map((d) => api.get(`${base}/${d.id}/form`).then((f) => [d.id, f]).catch(() => [d.id, { fields: [] }])))
      .then((rows) => setForms(Object.fromEntries(rows)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const blanks = useMemo(() => {
    const m = new Map();
    docs.forEach((d) => (forms?.[d.id]?.fields || []).filter((f) => f.editable).forEach((f) => {
      const k = keyOf(f);
      const b = m.get(k) || { key: k, label: f.label, required: false, titles: [] };
      b.required = (b.required || f.required) && !/\(if [^)]*\)/i.test(f.label);
      if (!b.titles.includes(d.title)) b.titles.push(d.title);
      m.set(k, b);
    }));
    return [...m.values()];
  }, [forms, docs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!forms) return;
    const a = {};
    docs.forEach((d) => (forms[d.id]?.fields || []).forEach((f) => { if (f.editable && seed[f.id]) a[keyOf(f)] = seed[f.id]; }));
    setAnswers(a);
  }, [forms]); // eslint-disable-line react-hooks/exhaustive-deps
  const readStart = blanks.length;
  const adoptStep = blanks.length + docs.length;
  const [readOk, setReadOk] = useState({});
  const reading = step >= readStart && step < adoptStep ? docs[step - readStart] : null;
  const readForm = reading && forms?.[reading.id] && {
    ...forms[reading.id],
    fields: forms[reading.id].fields.map((f) => (f.editable ? { ...f, editable: false, value: answers[keyOf(f)] || "" } : f)),
  };
  const boxRef = React.useRef(null);
  const endRef = React.useRef(null);
  useEffect(() => {
    if (!reading || !boxRef.current || !endRef.current) return undefined;
    const id = reading.id;
    const box = boxRef.current;
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setReadOk((r) => ({ ...r, [id]: true })), { root: box, threshold: 0.5 });
    const need = readForm.pages.length;
    const t = setInterval(() => {
      const imgs = [...box.querySelectorAll("img")];
      if (imgs.length >= need && imgs.every((i) => i.complete && i.naturalHeight > 0)) { clearInterval(t); io.observe(endRef.current); }
    }, 300);
    return () => { clearInterval(t); io.disconnect(); };
  }, [reading?.id, readForm ? 1 : 0]); // eslint-disable-line react-hooks/exhaustive-deps
  const b = blanks[step];
  const val = b ? answers[b.key] || "" : "";
  const signAll = async () => {
    setBusy(true); setErrors([]);
    const ok = [...done]; const bad = [];
    for (const d of docs) {
      if (ok.includes(d.id)) continue;
      const fills = Object.fromEntries((forms[d.id]?.fields || []).filter((f) => f.editable).map((f) => [f.id, answers[keyOf(f)] || ""]));
      try { await api.post(`${base}/${d.id}/sign`, { typed_name: name, agree, fills }); ok.push(d.id); } catch (e) { bad.push(`${d.title}: ${docsErr(e)}`); }
    }
    setDone(ok); setErrors(bad); setBusy(false);
  };
  const finished = done.length === docs.length && docs.length > 0;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose(done.length > 0)}>
      <DialogContent data-testid={prefix} className="bg-[#0b0f17] border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid={`${prefix}-title`}>{finished ? "All signed" : step < readStart ? "Fill in the blanks" : reading ? `Read: ${reading.title}` : "Adopt your signature"}</DialogTitle>
          <DialogDescription className="text-white/60">
            {docs.length} document{docs.length === 1 ? "" : "s"} · {finished ? "nothing left to do" : reading ? `Document ${step - readStart + 1} of ${docs.length} · scroll to the end to continue` : step < readStart ? `Question ${step + 1} of ${blanks.length}${docs.length > 1 ? " · you answer each one once, it fills every document" : ""}` : docs.length > 1 ? "your signature goes on every document below" : "your signature goes on this document"}
          </DialogDescription>
        </DialogHeader>
        {!forms ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : finished ? (
          <div className="space-y-4" data-testid={`${prefix}-done`}>
            <p className="text-emerald-300 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> You signed {docs.length} document{docs.length > 1 ? "s" : ""}.</p>
            <button type="button" onClick={() => onClose(true)} data-testid={`${prefix}-close`} className={`${btn} w-full bg-[#d4a017] text-black`}>Close</button>
          </div>
        ) : reading ? (
          <div className="space-y-3" data-testid={`${prefix}-read`}>
            <div key={reading.id} ref={boxRef} data-testid={`${prefix}-read-scroll`} className="max-h-[55vh] overflow-y-auto rounded-md border border-white/10 bg-slate-200 p-2">
              {readForm && <FilledPages api={api} base={base} qid={reading.id} form={readForm} fills={{}} setFills={() => {}} typedName="" prefix={`${prefix}-read`} />}
              {readForm && <div ref={endRef} data-testid={`${prefix}-read-end`} className="h-6 mt-2 text-center text-[11px] text-slate-500">End of document</div>}
            </div>
            {!readOk[reading.id] && <p className="text-xs text-amber-300" data-testid={`${prefix}-read-hint`}>Scroll through the whole document to continue.</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid={`${prefix}-read-back`} className={`${btn} border border-white/15`}>Back</button>
              <button type="button" disabled={!readOk[reading.id]} onClick={() => setStep(step + 1)} data-testid={`${prefix}-read-next`} className={`${btn} flex-1 bg-[#d4a017] text-black`}>I've read this document</button>
            </div>
          </div>
        ) : step < readStart ? (
          <div className="space-y-4">
            <div className="h-1 rounded bg-white/10"><div className="h-1 rounded bg-[#d4a017]" style={{ width: `${((step + 1) / (blanks.length + 1)) * 100}%` }} /></div>
            <label className="block space-y-1.5"><span className="text-base text-white">{b.label}{b.required && <span className="text-amber-300"> *</span>}</span>
              <input autoFocus value={val} onChange={(e) => setAnswers({ ...answers, [b.key]: e.target.value })} data-testid={`${prefix}-blank-input`}
                onKeyDown={(e) => e.key === "Enter" && (!b.required || val.trim()) && setStep(step + 1)}
                className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2.5 text-white outline-none focus:border-[#d4a017]" /></label>
            <p className="text-xs text-white/50" data-testid={`${prefix}-blank-docs`}>Used in: {b.titles.join(" · ")}</p>
            <div className="flex gap-2">
              {step > 0 && <button type="button" onClick={() => setStep(step - 1)} data-testid={`${prefix}-back`} className={`${btn} border border-white/15`}>Back</button>}
              <button type="button" disabled={b.required && !val.trim()} onClick={() => setStep(step + 1)} data-testid={`${prefix}-next`} className={`${btn} flex-1 bg-[#d4a017] text-black`}>Next</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="block space-y-1"><span className="text-sm text-white/70">Type your full name</span>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid={`${prefix}-name`} autoComplete="name"
                className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2.5 text-white outline-none focus:border-[#d4a017]" /></label>
            <div data-testid={`${prefix}-preview`} className="min-h-[64px] rounded-md bg-white px-4 py-2 text-4xl text-[#0b1f4d] border-b-2 border-slate-400" style={{ fontFamily: "'Great Vibes', cursive" }}>{name.trim() || <span className="text-base text-slate-400 font-sans">Your signature appears here</span>}</div>
            <ul className="text-xs text-white/60 list-disc pl-5" data-testid={`${prefix}-doc-list`}>{docs.map((d) => <li key={d.id} className={done.includes(d.id) ? "text-emerald-300" : ""}>{d.title}{done.includes(d.id) ? " ✓" : ""}</li>)}</ul>
            <label className="flex items-start gap-2 text-sm text-white/80"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid={`${prefix}-agree`} className="mt-1" />
              I have read these documents and agree to them. I adopt this as my signature, and my typed name is my legal signature on each one.</label>
            {errors.length > 0 && <ul data-testid={`${prefix}-errors`} className="text-sm text-red-300 space-y-1">{errors.map((x) => <li key={x}>{x}</li>)}</ul>}
            <div className="flex gap-2">
              {adoptStep > 0 && <button type="button" disabled={busy} onClick={() => setStep(step - 1)} data-testid={`${prefix}-back`} className={`${btn} border border-white/15`}>Back</button>}
              <button type="button" disabled={busy || !agree || name.trim().length < 2} onClick={signAll} data-testid={`${prefix}-submit`} className={`${btn} flex-1 bg-[#d4a017] text-black`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} Adopt & sign {docs.length > 1 ? `all ${docs.length}` : ""}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
